'use client';

import clsx from 'clsx';
import { Heading2 } from '@/components/section/headings';
import SiteInquiryForm from '@/components/contact/SiteInquiryForm';
import { SITE_CONTACT_EMAIL, SITE_CONTACT_MAILTO } from '@/constants/siteContact';
import type { InquiryTypeId } from '@/lib/contact/inquiryTypes';
import { H3, Li, P, Span, Ul, SURFACE_REVEAL, SURFACE_STATIC, surfaceCard } from '@/components/ui';
import type { SpecialStep } from '@/components/pages/marketing/special/types';

export type ConsultationInquirySectionProps = {
  sectionTitle?: string;
  sectionSubtitle?: string;
  stepsTitle: string;
  steps: readonly SpecialStep[];
  expectationsTitle?: string;
  expectations?: readonly string[];
  formTitle: string;
  formSubtitle: string;
  requestType?: InquiryTypeId;
  showTypeSelect?: boolean;
  formLabels: {
    submit: string;
    name: string;
    email: string;
    company: string;
    message: string;
  };
  contactEmailLabel?: string;
  className?: string;
};

/**
 * Consultation / contact — balanced two-column layout: process + checklist, sticky inquiry form.
 */
export default function ConsultationInquirySection({
  sectionTitle,
  sectionSubtitle,
  stepsTitle,
  steps,
  expectationsTitle,
  expectations = [],
  formTitle,
  formSubtitle,
  requestType = 'consultation',
  showTypeSelect = false,
  formLabels,
  contactEmailLabel = 'Prefer email?',
  className,
}: ConsultationInquirySectionProps) {
  return (
    <section
      className={clsx('page-consult-band container-section', className)}
      aria-labelledby={sectionTitle ? 'consult-section-heading' : 'consult-form-heading'}
    >
      {sectionTitle ? (
        <div className="page-heading-wrap" id="consult-section-heading">
          <Heading2
            title={sectionTitle}
            subtitle={sectionSubtitle}
            titleClassName="text-prose"
            subtitleClassName="mt-3 text-prose-muted"
          />
        </div>
      ) : null}

      <div className="page-content-grid page-consult-layout">
        <div className="min-w-0 space-y-8">
          <div>
            <H3 className="page-form-section-title">{stepsTitle}</H3>
            <ol className="page-consult-steps mt-4 list-none p-0">
              {steps.map((step, i) => (
                <li key={step.title} className={clsx('page-glass-step-card', SURFACE_REVEAL)}>
                  <Span className="page-consult-step__num" aria-hidden>
                    {i + 1}
                  </Span>
                  <div className="min-w-0 flex-1">
                    <H3 className="page-form-block-title">{step.title}</H3>
                    <P className="m-0 mt-1.5 text-sm leading-relaxed text-prose-muted">{step.text}</P>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {expectations.length > 0 && expectationsTitle ? (
            <div className={surfaceCard()}>
              <H3 className="page-form-block-title">{expectationsTitle}</H3>
              <Ul className="page-consult-checklist mt-4">
                {expectations.map((item) => (
                  <Li key={item} className="page-consult-checklist__item">
                    <Span className="page-consult-checklist__mark" aria-hidden>
                      ✓
                    </Span>
                    <Span>{item}</Span>
                  </Li>
                ))}
              </Ul>
            </div>
          ) : null}

          <div
            className={clsx(
              surfaceCard(),
              'page-glass-panel--compact page-glass-panel--strip page-glass-contact-strip',
              SURFACE_REVEAL
            )}
          >
            <P className="m-0 text-sm text-prose-muted">{contactEmailLabel}</P>
            <a
              href={SITE_CONTACT_MAILTO}
              className="page-contact-link"
            >
              {SITE_CONTACT_EMAIL}
            </a>
          </div>
        </div>

        <aside
          id="consult-form"
          className={clsx(
            'page-consult-form-panel page-glass-panel page-glass-panel--elevated min-w-0 lg:sticky lg:top-24 lg:self-start',
            SURFACE_STATIC
          )}
        >
          <H3 id="consult-form-heading" className="m-0 text-xl font-semibold tracking-tight text-prose sm:text-2xl">
            {formTitle}
          </H3>
          {formSubtitle ? (
            <P className="m-0 mt-2 text-sm leading-relaxed text-prose-muted sm:text-base">
              {formSubtitle}
            </P>
          ) : null}
          <SiteInquiryForm
            className="page-inquiry-form mt-6 sm:mt-8"
            defaultType={requestType}
            showTypeSelect={showTypeSelect}
            labels={formLabels}
          />
        </aside>
      </div>
    </section>
  );
}
